import { Component, ElementRef, OnDestroy, OnInit } from "@angular/core";
import { AccessControlService } from "app/access-control/access-control-service";
import { ACTIVITY_LOG } from "app/access-control/common-permission-asks";
import { ActivityLog } from "app/access-control/permissions";
import { PermissionsResponse } from "app/access-control/permissions-response";
import { ModalController, NavParams } from "ionic-angular";
import { DisciplineActivity } from "models/company-resources/activity-grid";
import SortOptions from "models/content-wrapper/sort-options";
import moment from "moment";
import { NavigationService } from "navigation/navigation.service";
import { disciplineList } from "pages/calendar/discipline-legend";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { SystemAdminService } from "providers/system-admin/system-admin.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { WorkforceActivity } from "../../../models/system-admin/workforce-activity";
import { WorkforceActivityItem } from "../../../models/system-admin/workforce-activity-item";
import { WorkforceContact } from "../../../models/system-admin/workforce-contact";
import { ActivityLogAddComponent } from "../activity-log-add/activity-log-add";

@Component({
  selector: "activity-log",
  templateUrl: "activity-log.html",
})
export class ActivityLogComponent implements OnInit, OnDestroy {
  userSearchSubject: Subject<string> = new Subject();
  private pageSize: number = 10;
  public currentPage: number = 0;
  public totalPageCount: number;
  private selectedStartDate: any;
  private selectedEndDate: any;
  private activityLog: WorkforceActivityItem[] = [];
  private query: string = "";
  public showDetails: boolean = false;
  disciplineList: DisciplineActivity[];
  disciplineSelected: string;
  public employeeList: WorkforceContact[];
  employeeSelected: number;
  private filterParameters: any = {};
  minDateValue: any = { year: 1900, month: 1, day: 1 };
  activityLogDetail: WorkforceActivity;
  sortOptions: SortOptions = new SortOptions("date", "desc");
  subscriptions: Subscription[] = [];
  activityLogPermissionsAccess = {} as PermissionsResponse;
  hasActivityLogAll: boolean = false;
  hacActivityLogView: boolean = false;
  hasActivityLogAdd: boolean = false;
  hasActivityLogEdit: boolean = false;
  hasActivityLogDelete: boolean = false;
  private boundOnSearchIconClick: () => void;
  private searchIconEl: Element | null = null;

  constructor(
    private utilService: UtilityService,
    private resourceManagementService: ResourceManagementService,
    private systemAdminService: SystemAdminService,
    public modalController: ModalController,
    public navService: NavigationService,
    private elementRef: ElementRef,
    private accessControlService: AccessControlService,
    public navParams: NavParams
  ) {
    this.disciplineList = disciplineList;
    this.checkActivityLogPermissionsAccess();
  }

  ngAfterViewInit() {
    // attaching event on search icon explictly
    this.searchIconEl = this.elementRef.nativeElement.querySelector(
      ".searchbar-search-icon"
    );
    if (this.searchIconEl) {
      this.boundOnSearchIconClick = () => this.loadCompanyResource();
      this.searchIconEl.addEventListener("click", this.boundOnSearchIconClick);
    }
  }

  ngOnDestroy(): void {
    if (this.searchIconEl && this.boundOnSearchIconClick) {
      this.searchIconEl.removeEventListener(
        "click",
        this.boundOnSearchIconClick
      );
    }
  }

  checkActivityLogPermissionsAccess() {
    this.subscriptions.push(
      this.accessControlService
        .getPermission(ACTIVITY_LOG)
        .subscribe((data) => {
          if (data) {
            this.activityLogPermissionsAccess = data;
            this.getTeamsAccess();
          }
        })
    );
  }

  getTeamsAccess() {
    this.hasActivityLogAll = this.accessControlService.hasAccess(
      this.activityLogPermissionsAccess,
      ActivityLog.ActivityLogAll
    );
    this.hacActivityLogView = this.accessControlService.hasAccess(
      this.activityLogPermissionsAccess,
      ActivityLog.ActivityLogView
    );
    this.hasActivityLogAdd = this.accessControlService.hasAccess(
      this.activityLogPermissionsAccess,
      ActivityLog.ActivityLogAdd
    );
    this.hasActivityLogEdit = this.accessControlService.hasAccess(
      this.activityLogPermissionsAccess,
      ActivityLog.ActivityLogEdit
    );
    this.hasActivityLogDelete = this.accessControlService.hasAccess(
      this.activityLogPermissionsAccess,
      ActivityLog.ActivityLogDelete
    );
  }

  ngOnInit(): void {
    this.showDetails = false;
    this.subscribeUserSearch();
    this.searchUsers("");
    this.getMinDate();
    this.loadCompanyResource();

    const momentDate = moment();
    this.minDateValue = {
      year: momentDate.get("year") - 10,
      month: momentDate.get("month") + 1,
      day: momentDate.date(),
    };
  }

  private getMinDate() {
    const momentDate = moment();
    this.minDateValue = {
      year: momentDate.get("year") - 10,
      month: momentDate.get("month") + 1,
      day: momentDate.date(),
    };
  }

  private subscribeUserSearch() {
    this.userSearchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((x: string) => {
        if (!x || x.length === 0) {
          this.employeeList = [];
        } else if (x.length > 2) {
          this.searchUsers(x);
        }
      });
  }

  loadCompanyResource(selectedPage: number = 0): void {
    this.utilService.increaseSpinnerCount("Loading activity log");
    this.currentPage = typeof selectedPage === "number" ? selectedPage : 0;

    this.loadFilters();

    this.systemAdminService.getActivities(this.filterParameters).subscribe(
      (response) => {
        this.activityLog = [];
        // Reset alongside the rows: leaving the previous count here left an empty
        // grid still advertising the OLD number of pages in its pager.
        this.totalPageCount = 0;
        if (response && response["hits"] && response["totalHits"] > 0) {
          this.activityLog = response["hits"] as WorkforceActivityItem[];
          this.activityLog = this.activityLog.map((el) => {
            let lastName =
              el.userLastName !== null &&
              el.userLastName !== undefined &&
              el.userLastName !== ""
                ? el.userLastName + ","
                : "";
            let firstName =
              el.userFirstName !== null &&
              el.userFirstName !== undefined &&
              el.userFirstName !== ""
                ? el.userFirstName
                : "";
            let middleInitial =
              el.userMiddleInitial !== null &&
              el.userMiddleInitial !== undefined &&
              el.userMiddleInitial !== ""
                ? "," + el.userMiddleInitial
                : "";

            let completeName = lastName + firstName + middleInitial;

            return {
              ...el,
              name: completeName,
              durationDisplay: moment
                .utc(el.totalTime * 60000)
                .format("H [hr] m [min]"),
              travelTimeDisplay: moment
                .utc(el.travelTime * 60000)
                .format("H [hr] m [min]"),
            };
          });
          this.totalPageCount = Math.ceil(
            response["totalHits"] / this.pageSize
          );
          this.currentPage =
            this.currentPage !== null &&
            this.currentPage !== undefined &&
            typeof selectedPage === "number"
              ? this.currentPage
              : 0;
        }
        this.utilService.decreaseSpinnerCount();
      },
      (err) => {
        this.utilService.decreaseSpinnerCount();
        let msg = "Error loading activities";
        console.error(err);
        this.utilService.presentErrorToast(err, msg);
      }
    );
  }

  loadFilters() {
    this.filterParameters.entityType = "EMPLOYEEACTIVITYLOG";
    this.filterParameters.from = this.currentPage * this.pageSize;
    this.filterParameters.size = this.pageSize;
    this.filterParameters.mustFields = [];
    this.filterParameters.sortBy = {
      name: this.sortOptions.column,
      value: this.sortOptions.direction,
    };

    this.filterParameters.dateRanges = this.loadRangeDates();

    if (this.disciplineSelected) {
      this.filterParameters.mustFields.push({
        name: "discipline",
        value: this.disciplineSelected,
      });
    }

    if (this.employeeSelected) {
      this.filterParameters.mustFields.push({
        name: "userId",
        value: this.employeeSelected.toString(),
      });
    }

    if (this.query) {
      this.filterParameters.search = this.query;
    }
  }

  loadRangeDates() {
    if (!this.selectedStartDate && !this.selectedEndDate) {
      return null;
    } else {
      let dateRange: any = {};
      dateRange.name = "date";

      if (this.selectedStartDate) {
        dateRange.fromDate = moment(this.selectedStartDate).format(
          "YYYY-MM-DD"
        );
      }

      if (this.selectedEndDate) {
        dateRange.toDate = moment(this.selectedEndDate).format("YYYY-MM-DD");
      }

      return dateRange;
    }
  }

  searchUserInputChanged(e) {
    let searchTerm = e.target.value;
    this.userSearchSubject.next(searchTerm);
  }

  searchUsers(search: string) {
    this.employeeList = [];
    this.resourceManagementService
      .getContactActivityLogSearch(search, true)
      .pipe()
      .subscribe((x) => {
        this.employeeList = x;
      });
  }

  onPageChange(selectedPage: number) {
    this.loadCompanyResource(selectedPage);
  }

  addActivityLog() {
    let modal = this.modalController.create(
      ActivityLogAddComponent,
      {
        grid: this,
        page: this.currentPage,
      },
      { enableBackdropDismiss: false }
    );
    modal
      .present()
      .then((res) => {})
      .catch((err) => {
        console.error(err);
      });
    modal.onDidDismiss(() => {
      setTimeout(() => {
        this.loadCompanyResource();
      }, 1500);
    });
  }

  onSearchCancel() {
    this.query = null;
    if (this.filterParameters && this.filterParameters.search) {
      this.filterParameters.search = null;
    }
    this.loadCompanyResource();
  }

  checkClearInput() {
    if (!this.query || this.query.trim().length === 0) {
      this.onSearchCancel();
    }
  }

  validateDate(date, field) {
    const momentDate = moment(date);
    if (date) {
      if (field === "startDate") {
        this.minDateValue = {
          year: momentDate.get("year"),
          month: momentDate.get("month") + 1,
          day: momentDate.date(),
        };
      }

      if (
        moment(this.selectedStartDate).isAfter(moment(this.selectedEndDate))
      ) {
        this.selectedEndDate = this.selectedStartDate;
      }
    } else if (field === "startDate") {
      this.minDateValue = {
        year: momentDate.get("year") - 10,
        month: 1,
        day: 1,
      };
    }
  }

  showActivityDetails(a: WorkforceActivity) {
    this.activityLogDetail = a;
    this.showDetails = true;
  }

  printActivities() {
    print();
  }

  showActivityReport() {
    this.navService.goToInternalPage("ActivityLogReportPage", {
      // companyData: this.navParams.get("companyData"),
      // location: this.navParams.get("location")
    });
  }

  hideDetails(action: string) {
    this.showDetails = false;
    this.loadCompanyResource();
  }

  clearFilters() {
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.query = null;
    this.employeeSelected = null;
    this.disciplineSelected = null;
    this.filterParameters = {};
    this.searchUsers("");
    this.getMinDate();
    this.loadCompanyResource();
  }

  sortBy(selectedCol) {
    this.currentPage = 0;
    if (this.sortOptions.column === selectedCol) {
      this.sortOptions.direction =
        this.sortOptions.direction === "asc" ? "desc" : "asc";
    } else {
      this.sortOptions.column = selectedCol;
      this.sortOptions.direction = "asc";
    }

    this.loadCompanyResource();
  }
}



// WEBPACK FOOTER //
// ./src/components/system-admin/activity-log/activity-log.ts